import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { Activity, CheckCircle2, Edit3, RefreshCw, Save, Shield, XCircle, Zap } from "lucide-react"
import { smartoltService, type SmartOLTSystemSettings } from "../../../services/smartolt.service.ts"
import { toast } from "../../../utils/notify.ts"
import { SMARTOLT_PATHS } from "../smartoltNav.ts"
import { formatUpdated } from "./smartoltSettingsUtils.ts"

const empty: SmartOLTSystemSettings = {
  tenant: { enabled: false, subdomain: null, base_url: null, budget_per_hour: 1000 },
  thresholds: { capacity_max_onus: 128, capacity_oversub_pct: 90 },
  policy: { auto_suspend_overdue: false },
  sync: { jobs: [], status_counts: {} },
  budget: {},
  updated_at: null,
}

const inputCls = "mt-1 block h-9 w-28 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500/20"
const tenantInputCls = "mt-1 h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 shadow-xs outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/15"
const cardCls = "rounded-2xl border border-slate-200/80 bg-white p-5 shadow-xs"

export function SmartOLTSystemConfigPage() {
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [data, setData] = useState<SmartOLTSystemSettings>(empty)
  const [maxOnus, setMaxOnus] = useState("128")
  const [oversubPct, setOversubPct] = useState("90")
  const [policy, setPolicy] = useState(empty.policy)
  const [editingTenant, setEditingTenant] = useState(false)
  const [tenantForm, setTenantForm] = useState({
    enabled: false,
    subdomain: "",
    base_url: "",
    budget_per_hour: "1000",
    api_key: "",
  })

  const syncTenantForm = (next: SmartOLTSystemSettings) => {
    setTenantForm({
      enabled: Boolean(next?.tenant?.enabled),
      subdomain: next?.tenant?.subdomain || "",
      base_url: next?.tenant?.base_url || "",
      budget_per_hour: String(next?.budget?.budget_per_hour ?? next?.tenant?.budget_per_hour ?? 1000),
      api_key: "",
    })
  }

  const load = async (silent = false) => {
    if (!silent) setLoading(true)
    setRefreshing(true)
    try {
      const next = await smartoltService.getSystemSettings()
      setData({
        tenant: next?.tenant || empty.tenant,
        thresholds: next?.thresholds || empty.thresholds,
        policy: next?.policy || empty.policy,
        sync: next?.sync || empty.sync,
        budget: next?.budget || empty.budget,
        updated_at: next?.updated_at || null,
      })
      setMaxOnus(String(next?.thresholds?.capacity_max_onus ?? 128))
      setOversubPct(String(next?.thresholds?.capacity_oversub_pct ?? 90))
      setPolicy(next?.policy || empty.policy)
      syncTenantForm(next || empty)
    } catch {
      toast.error("Failed to load system settings")
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const saveThresholds = async () => {
    setSaving(true)
    try {
      await smartoltService.updateCapacityThresholds({
        capacity_max_onus: Number(maxOnus),
        capacity_oversub_pct: Number(oversubPct),
      })
      toast.success("Capacity thresholds saved")
      await load(true)
    } catch {
      toast.error("Failed to save thresholds")
    } finally {
      setSaving(false)
    }
  }

  const saveTenant = async () => {
    const budget = Number(tenantForm.budget_per_hour)
    if (!Number.isFinite(budget) || budget < 100) {
      toast.error("API budget must be at least 100 calls per hour")
      return
    }
    setSaving(true)
    try {
      await smartoltService.updateTenantConfig({
        subdomain: tenantForm.subdomain.trim() || null,
        base_url: tenantForm.base_url.trim() || null,
        budget_per_hour: budget,
        ...(tenantForm.api_key.trim() ? { api_key: tenantForm.api_key.trim() } : {}),
        ...(data.tenant.can_manage_module_access === false ? {} : { enabled: tenantForm.enabled }),
      })
      toast.success("SmartOLT tenant settings saved")
      setEditingTenant(false)
      await load(true)
    } catch (error: any) {
      toast.error(error?.response?.data?.error || "Failed to save tenant settings")
    } finally {
      setSaving(false)
    }
  }

  const savePolicy = async () => {
    setSaving(true)
    try {
      await smartoltService.updateAutomationPolicy(policy)
      toast.success("Billing policy saved")
      await load(true)
    } catch {
      toast.error("Failed to save policy")
    } finally {
      setSaving(false)
    }
  }

  const runSync = async () => {
    setSaving(true)
    try {
      await smartoltService.runSyncNow()
      toast.success("Sync jobs triggered")
      await load(true)
    } catch {
      toast.error("Sync failed")
    } finally {
      setSaving(false)
    }
  }

  const errorJobs = [...(data.sync.jobs || [])].filter((job) => job.status === "error" || job.last_error)

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">System Config</h1>
          <p className="mt-0.5 text-xs text-slate-500">Sync, capacity thresholds, and billing automation</p>
        </div>
        <button type="button" disabled={refreshing} onClick={() => load(true)}
          className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 shadow-xs transition-colors hover:bg-slate-50 hover:text-blue-600 disabled:opacity-50">
          <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="rounded-2xl border border-slate-200/80 bg-white px-4 py-12 text-center shadow-xs">
          <RefreshCw className="mx-auto h-7 w-7 animate-spin text-blue-500 mb-2" />
          <p className="text-slate-500 text-sm">Loading system settings…</p>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Tenant card */}
          <div className={cardCls}>
            <div className="flex items-center gap-2 mb-4">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50">
                <Shield className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-slate-800">Tenant</h2>
                <p className="text-[11px] text-slate-500">Company SmartOLT connection settings</p>
              </div>
              <span className={`ml-auto rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${
                data.tenant.enabled
                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                  : "bg-slate-100 text-slate-600 border border-slate-200"
              }`}>
                {data.tenant.enabled ? "Enabled" : "Disabled"}
              </span>
            </div>
            {editingTenant ? (
              <div className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <label className="text-xs font-medium text-slate-700">
                    Subdomain
                    <input
                      value={tenantForm.subdomain}
                      onChange={(e) => setTenantForm((prev) => ({ ...prev, subdomain: e.target.value }))}
                      placeholder="hajverynetworks"
                      className={tenantInputCls}
                    />
                  </label>
                  <label className="text-xs font-medium text-slate-700">
                    Base URL override
                    <input
                      value={tenantForm.base_url}
                      onChange={(e) => setTenantForm((prev) => ({ ...prev, base_url: e.target.value }))}
                      placeholder="https://example.smartolt.com"
                      className={tenantInputCls}
                    />
                  </label>
                  <label className="text-xs font-medium text-slate-700">
                    API budget per hour
                    <input
                      value={tenantForm.budget_per_hour}
                      onChange={(e) => setTenantForm((prev) => ({ ...prev, budget_per_hour: e.target.value }))}
                      type="number"
                      min={100}
                      className={tenantInputCls}
                    />
                  </label>
                  <label className="text-xs font-medium text-slate-700">
                    API key
                    <input
                      value={tenantForm.api_key}
                      onChange={(e) => setTenantForm((prev) => ({ ...prev, api_key: e.target.value }))}
                      type="password"
                      placeholder="Leave blank to keep current key"
                      className={tenantInputCls}
                    />
                  </label>
                </div>
                {data.tenant.can_manage_module_access !== false && (
                  <label className="inline-flex cursor-pointer items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-medium text-slate-700">
                    <input
                      type="checkbox"
                      checked={tenantForm.enabled}
                      onChange={(e) => setTenantForm((prev) => ({ ...prev, enabled: e.target.checked }))}
                      className="h-4 w-4 rounded border-slate-300 bg-white text-blue-600 focus:ring-blue-500"
                    />
                    Enable SmartOLT for this company
                  </label>
                )}
                <div className="flex flex-wrap justify-end gap-2">
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => {
                      syncTenantForm(data)
                      setEditingTenant(false)
                    }}
                    className="h-9 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={saving}
                    onClick={saveTenant}
                    className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-blue-600 px-4 text-sm font-medium text-white shadow-xs transition-colors hover:bg-blue-700 disabled:opacity-50"
                  >
                    <Save className="h-3.5 w-3.5" />
                    Save Tenant
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-4 text-xs sm:grid-cols-4">
                  {[
                    ["Subdomain", data.tenant.subdomain || "—"],
                    ["Base URL", data.tenant.base_url || "—"],
                    ["API Budget", `${data.budget.used_calls ?? 0}/${data.budget.budget_per_hour ?? data.tenant.budget_per_hour}`],
                    ["Last Updated", formatUpdated(data.updated_at)],
                  ].map(([label, value]) => (
                    <div key={label as string}>
                      <p className="text-slate-500">{label}</p>
                      <p className="mt-1 font-semibold text-slate-800 truncate">{value as string}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-4 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setEditingTenant(true)}
                    className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 shadow-xs transition-colors hover:bg-slate-50 hover:text-blue-600"
                  >
                    <Edit3 className="h-3.5 w-3.5" />
                    Edit Tenant
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Capacity thresholds */}
          <div className={cardCls}>
            <div className="flex items-center gap-2 mb-4">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-50">
                <Activity className="h-4 w-4 text-amber-600" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-slate-800">Capacity Thresholds</h2>
                <p className="text-[11px] text-slate-500">
                  Used on the <Link to={SMARTOLT_PATHS.capacity} className="text-blue-600 hover:underline">Capacity</Link> page
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-end gap-4">
              <label className="text-xs font-medium text-slate-700">
                Max ONUs per PON
                <input value={maxOnus} onChange={(e) => setMaxOnus(e.target.value)} type="number" className={inputCls} />
              </label>
              <label className="text-xs font-medium text-slate-700">
                Oversubscription %
                <input value={oversubPct} onChange={(e) => setOversubPct(e.target.value)} type="number" className={inputCls} />
              </label>
              <button type="button" disabled={saving} onClick={saveThresholds}
                className="h-9 rounded-lg bg-blue-600 hover:bg-blue-700 px-4 text-sm font-medium text-white shadow-xs disabled:opacity-50 transition-colors">
                Save Thresholds
              </button>
            </div>
          </div>

          {/* Billing policy */}
          <div className={cardCls}>
            <div className="flex items-center gap-2 mb-4">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-50">
                <Zap className="h-4 w-4 text-emerald-600" />
              </div>
              <h2 className="text-sm font-semibold text-slate-800">Billing Policy</h2>
            </div>
            <div className="flex flex-wrap gap-4 text-sm">
              <label className="inline-flex cursor-pointer items-center gap-3">
                <input
                  type="checkbox"
                  id="billing-policy-auto-suspend"
                  checked={policy.auto_suspend_overdue}
                  onChange={(e) => setPolicy((prev) => ({ ...prev, auto_suspend_overdue: e.target.checked }))}
                  className="h-4 w-4 rounded border-slate-300 bg-white text-blue-600 focus:ring-blue-500"
                />
                <span className="text-slate-700 font-medium">Auto disable ONU on overdue invoices</span>
              </label>
            </div>
            <p className="mt-2 text-xs text-slate-500">
              When enabled, the billing enforcement worker will automatically suspend ONUs for
              customers with unpaid invoices past the 24-hour grace period. Disabling this
              checkbox stops the worker entirely — no suspensions will be applied or enforced.
              ONUs are always re-enabled automatically when dues are cleared.
            </p>
            <button type="button" disabled={saving} onClick={savePolicy}
              className="mt-4 h-9 rounded-lg bg-emerald-600 hover:bg-emerald-700 px-4 text-sm font-medium text-white shadow-xs disabled:opacity-50 transition-colors">
              Save Policy
            </button>
          </div>

          {/* Sync jobs */}
          <div className={cardCls}>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-50">
                  <RefreshCw className="h-4 w-4 text-indigo-600" />
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-slate-800">Sync Jobs</h2>
                  <p className="text-[11px] text-slate-500">
                    {data.sync.jobs.length} jobs
                    {errorJobs.length > 0 && (
                      <span className="ml-1 text-rose-600 font-semibold">· {errorJobs.length} need attention</span>
                    )}
                  </p>
                </div>
              </div>
              <button type="button" disabled={saving} onClick={runSync}
                className="h-9 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 shadow-xs transition-colors hover:bg-slate-50 hover:text-blue-600 disabled:opacity-50">
                Run sync now
              </button>
            </div>
            <div className="max-h-64 overflow-auto rounded-xl border border-slate-200 divide-y divide-slate-100">
              {data.sync.jobs.map((job) => {
                const hasError = job.status === "error" || job.last_error
                return (
                  <div key={job.job_key} className={`px-4 py-3 text-xs ${hasError ? "bg-rose-50/50" : "hover:bg-slate-50/60"}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-slate-800">{job.job_key}</span>
                      <span className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${
                        hasError
                          ? "bg-rose-50 text-rose-700 border border-rose-200"
                          : "bg-emerald-50 text-emerald-700 border border-emerald-200"
                      }`}>
                        {hasError ? <XCircle className="h-2.5 w-2.5" /> : <CheckCircle2 className="h-2.5 w-2.5" />}
                        {job.status}
                      </span>
                    </div>
                    <p className="mt-1 text-slate-500 font-mono">
                      Last {formatUpdated(job.last_run_at)} · next {formatUpdated(job.next_run_at)}
                    </p>
                    {job.last_error && <p className="mt-1 text-rose-600 font-semibold">{job.last_error}</p>}
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
